import { cn } from "@/lib/cn";
import type { HTMLAttributes, ReactNode } from "react";
import type { SessionState, VerificationLevel } from "@/lib/mock/types";

/*
  Badge — dos usos principales:
  1) <Badge variant="..."> genérico (tono semántico).
  2) Helpers específicos del dominio:
     - <VerificationBadge level="L2" /> — nivel de verificación (§4, §6).
     - <SessionStateBadge state="checked_in" /> — estado de la máquina de sesión.
  Los TEXTOS visibles deberían venir de next-intl en la superficie; estos helpers
  traen un label por defecto en español para prototipos rápidos, sobrescribible
  con `label`.
*/

type Tone =
  | "neutral"
  | "sand"
  | "forest"
  | "clay"
  | "success"
  | "warning"
  | "danger"
  | "info";

const tones: Record<Tone, string> = {
  neutral: "bg-sand-100 text-ink-muted border-sand-300",
  sand: "bg-sand-200 text-ink-soft border-sand-300",
  forest: "bg-forest-100 text-forest-strong border-forest-200",
  clay: "bg-clay-100 text-clay-strong border-clay-200",
  success: "bg-forest-100 text-success border-forest-200",
  warning: "bg-[#f5e9d2] text-warning border-[#e6d3ad]",
  danger: "bg-[#f2ddd6] text-danger border-[#e2c2b8]",
  info: "bg-[#dde8ee] text-info border-[#c2d6de]",
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: Tone;
  /** Punto de color a la izquierda (útil para estados de sesión). */
  dot?: boolean;
  children?: ReactNode;
}

export function Badge({
  variant = "neutral",
  dot = false,
  className,
  children,
  ...props
}: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5",
        "text-xs font-medium leading-5 tracking-wide",
        tones[variant],
        className,
      )}
      {...props}
    >
      {dot && (
        <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      )}
      {children}
    </span>
  );
}

/* ── Verificación L0 / L1 / L2 ───────────────────────────────────────────── */

const LEVEL_TONE: Record<VerificationLevel, Tone> = {
  L0: "neutral", // sólo email
  L1: "clay", // tel + tarjeta
  L2: "forest", // ID + selfie (confianza plena — gate de reserva)
};

const LEVEL_LABEL_ES: Record<VerificationLevel, string> = {
  L0: "L0 · Email",
  L1: "L1 · Tel + tarjeta",
  L2: "L2 · Identidad verificada",
};

export function VerificationBadge({
  level,
  label,
  className,
}: {
  level: VerificationLevel;
  label?: string;
  className?: string;
}) {
  return (
    <Badge variant={LEVEL_TONE[level]} dot className={className}>
      {label ?? LEVEL_LABEL_ES[level]}
    </Badge>
  );
}

/* ── Estados de la máquina de sesión ─────────────────────────────────────── */

const STATE_TONE: Record<SessionState, Tone> = {
  requested: "sand",
  accepted: "info",
  paid: "info",
  en_route: "info",
  checked_in: "forest",
  in_progress: "forest",
  checked_out: "success",
  completed: "success",
  reviewed: "neutral",
  cancelled_by_client: "neutral",
  cancelled_by_therapist: "neutral",
  no_show_client: "warning",
  no_show_access_denied: "warning",
  safe_exit: "danger",
  sos_active: "danger",
  disputed: "warning",
};

const STATE_LABEL_ES: Record<SessionState, string> = {
  requested: "Solicitada",
  accepted: "Aceptada",
  paid: "Pagada",
  en_route: "En camino",
  checked_in: "Check-in",
  in_progress: "En curso",
  checked_out: "Check-out",
  completed: "Completada",
  reviewed: "Reseñada",
  cancelled_by_client: "Cancelada (cliente)",
  cancelled_by_therapist: "Cancelada (terapeuta)",
  no_show_client: "No-show cliente",
  no_show_access_denied: "Acceso denegado",
  safe_exit: "Salida segura",
  sos_active: "SOS activo",
  disputed: "En disputa",
};

export function SessionStateBadge({
  state,
  label,
  className,
}: {
  state: SessionState;
  label?: string;
  className?: string;
}) {
  return (
    <Badge variant={STATE_TONE[state]} dot className={className}>
      {label ?? STATE_LABEL_ES[state]}
    </Badge>
  );
}
